import { useState, useEffect, useCallback, useRef } from 'react'
import { useSocket } from '../contexts/SocketContext'
import { apiGet } from '../utils/api'

export interface Visitor {
  id: string
  customer_id: string | null
  fingerprint: string
  ip_address: string
  city: string
  country: string
  region: string
  browser: string
  os: string
  device_type: string
  referral_source: string
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  first_seen_at: string
  last_seen_at: string
  visit_count: number
  total_time_seconds: number
  lead_score: number
  visitor_type: 'new' | 'returning'
  action_label: string
  is_online: number
  current_page_url: string | null
  current_page_title: string | null
  page_count?: number
  wequote_account_id?: string
  plan_tier?: string
}

export interface VisitorStats {
  total_visitors: number
  online_now: number
  new_today: number
  returning_today: number
  avg_lead_score: number
  avg_time_on_site: number
  hourly_traffic: Array<{ hour: string; views: number }>
  top_pages: Array<{ page_url: string; page_title: string; views: number; avg_duration: number }>
  sources: Array<{ referral_source: string; count: number }>
  countries: Array<{ country: string; count: number }>
}

export function useVisitors() {
  const [visitors, setVisitors] = useState<Visitor[]>([])
  const [stats, setStats] = useState<VisitorStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'online' | 'offline'>('all')
  const [sortBy, setSortBy] = useState<string>('recent')
  const { socket } = useSocket()
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchVisitors = useCallback(async () => {
    try {
      const params: Record<string, string> = {}
      if (filter !== 'all') params.status = filter
      if (sortBy === 'lead_score') params.sort = 'lead_score'
      else if (sortBy === 'time') params.sort = 'time'
      else if (sortBy === 'pages') params.sort = 'pages'

      const data = await apiGet<{ visitors: Visitor[]; total: number }>('/visitors', params)
      setVisitors(data.visitors || [])
    } catch (err) {
      console.error('Failed to fetch visitors:', err)
    } finally {
      setLoading(false)
    }
  }, [filter, sortBy])

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiGet<VisitorStats>('/visitors/stats/summary')
      setStats(data)
    } catch (err) {
      console.error('Failed to fetch visitor stats:', err)
    }
  }, [])

  const debouncedFetch = useCallback(() => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)
    fetchTimerRef.current = setTimeout(() => {
      fetchVisitors()
      fetchStats()
    }, 300)
  }, [fetchVisitors, fetchStats])

  // Initial fetch
  useEffect(() => {
    fetchVisitors()
    fetchStats()
  }, [fetchVisitors, fetchStats])

  // Socket listeners — BUG-01: use named refs for proper cleanup
  useEffect(() => {
    if (!socket) return

    socket.emit('join_visitors')

    // BUG-01: Store handler reference so socket.off() can remove the exact listener
    const handleHeartbeat = () => { fetchStats() }

    socket.on('visitor_update', debouncedFetch)
    socket.on('visitor_offline', debouncedFetch)
    socket.on('visitor_page_view', debouncedFetch)
    socket.on('visitor_heartbeat', handleHeartbeat)

    return () => {
      // BUG-05: Clear debounce timer on unmount
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current)
        fetchTimerRef.current = null
      }
      socket.off('visitor_update', debouncedFetch)
      socket.off('visitor_offline', debouncedFetch)
      socket.off('visitor_page_view', debouncedFetch)
      socket.off('visitor_heartbeat', handleHeartbeat)
    }
  }, [socket, debouncedFetch, fetchStats])

  return {
    visitors,
    stats,
    loading,
    filter,
    setFilter,
    sortBy,
    setSortBy,
    refresh: debouncedFetch,
  }
}
